import { FC, useEffect, useState } from 'react'
import closeIcon from '../images/close-icon.svg'
import attachFileIcon from '../images/attach-file-icon.svg'
import deleteIcon from '../images/delete-icon.svg'
import SnackbarAlert from './SnackbarAlert'
import { Cookies } from 'react-cookie'
import { useFetch } from '../hooks/useFetch'
import LinearProgressBar from './LinearProgressBar'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import Skeleton from '@material-ui/lab/Skeleton'

interface Props {
    semesterId: number
    courseId: number
    handleClickClose: () => void
    isStudent?: boolean
}

const AddNewCommonGoalAndStudents: FC<Props> = ({
    semesterId, courseId, handleClickClose, isStudent
}) => {

    const [loading, setLoading] = useState(false)

    const [semesterName, setSemesterName] = useState<string>()
    const [courseName, setCourseName] = useState<string>()
    const [courseDesc, setCourseDesc] = useState<string>()

    const [excelFile, setExcelFile] = useState<File | null>(null)

    const [snackbarMessage, setSnackbarMessage] = useState<null | {
        message: string,
        severity?: 'success' | 'info' | 'warning' | 'error',
        autoHideDuration?: number | null
    }>(null)

    const cookies = new Cookies()

    const navigate = useNavigate()

    const { data: getSemestersData, error: getSemestersError, loading: getSemestersLoading } = useFetch({
        url: `/Semesters`,
        method: 'get',
        headers: {
            'Authorization': `Bearer ${cookies.get('user')?.token}`
        }
    })

    const { data: getCoursesData, error: getCoursesError, loading: getCoursesLoading } = useFetch({
        url: `/Curriculums`,
        method: 'get',
        headers: {
            'Authorization': `Bearer ${cookies.get('user')?.token}`
        }
    })

    useEffect(() => {
        if (getSemestersData) {
            const semester = getSemestersData?.find((semester: any) => semester.id === semesterId)
            setSemesterName(`${semester?.year}/${semester?.no}`)
        }
    }, [getSemestersData])

    useEffect(() => {
        if (getCoursesData) {
            const course = getCoursesData?.find((course: any) => course.id === courseId)
            setCourseName(`${course?.plan} / ${course?.code || ''} / ${course?.curriculumTh}`)
            setCourseDesc(course?.description)
        }
    }, [getCoursesData])

    const handleOnFileChange = (e: any) => {
        setSnackbarMessage(null)
        const file = e.target.files[0]
        if (file.size > 25_000_000) {
            setSnackbarMessage({
                message: 'File size must be less than 25MB',
                severity: 'error',
                autoHideDuration: 3000
            })
            return
        }

        if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            setSnackbarMessage({
                message: 'File type is not supported',
                severity: 'error',
                autoHideDuration: 3000
            })
            return
        } else {
            setExcelFile(file)
        }
    }

    const handleOnFileDelete = () => {
        setExcelFile(null)
        const file = document.querySelector('#upload-excel-file') as HTMLInputElement
        file.value = ''
    }

    const handleClickAdd = () => {
        setLoading(true)
        setSnackbarMessage(null)
        if (excelFile === null) {
            setLoading(false)
            setSnackbarMessage({
                message: 'Please select excel file',
                severity: 'error',
                autoHideDuration: 3000
            })
            return
        }
        const fetchURL = isStudent ? `Students` : `CommonGoals`
        console.log(cookies.get('user')?.staffId)
        axios.post(`/${fetchURL}?semesterId=${semesterId}&curriculumId=${courseId}`, {
            excel: excelFile,
            staffId: cookies.get('user')?.staffId
        }, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${cookies.get('user')?.token}`
            }
        }).then((res) => {
            setSnackbarMessage({
                message: `Add new ${isStudent ? 'students' : 'common goals'} successfully`,
                severity: 'success',
                autoHideDuration: 2000
            })
            setTimeout(() => {
                handleClickClose()
                setLoading(false)
                navigate(0)
            }, 2000)
        }
        ).catch((err) => {
            setLoading(false)
            if (err.response.status === 400) {
                setSnackbarMessage({ message: 'Please fill in all required fields in excel', severity: 'error', autoHideDuration: 3000 })
            } else {
                setSnackbarMessage({
                    message: 'Something went wrong',
                    severity: 'error',
                    autoHideDuration: 3000
                })
            }
        })
    }

    const studentsTemplate = require('../files/Student_Information_Template.xlsx')

    const commonGoalTemplate1 = require('../files/Common_Goal_Template-Master_A1.xlsx')
    const commonGoalTemplate2 = require('../files/Common_Goal_Template-Master_A2.xlsx')
    const commonGoalTemplate3 = require('../files/Common_Goal_Template-PhD_1-1.xlsx')
    const commonGoalTemplate4 = require('../files/Common_Goal_Template-PhD_1-2.xlsx')
    const commonGoalTemplate5 = require('../files/Common_Goal_Template-PhD_2-1.xlsx')
    const commonGoalTemplate6 = require('../files/Common_Goal_Template-PhD_2-2.xlsx')

    return (
        <div className='EditStudentInfoDialog'>
            <div className='EditStudentInfoDialog-dialog'>
                <button className="ProgressDialog-close-button" onClick={handleClickClose}>
                    <img src={closeIcon} width={24} height={24} alt="close-icon" />
                </button>
                {
                    (loading || getSemestersLoading || getCoursesLoading) &&
                    <div className="ProgressDialog-linear-progress">
                        <LinearProgressBar />
                    </div>
                }
                <div className="AddStudentDialog-header-container">
                    <h4 className='AddStudentDialog-title'>Add</h4>
                    <h4 className='AddStudentDialog-goal-name'>{"New " + (isStudent ? "Students" : "Common Goal")}</h4>
                </div>
                <div className='course-semester-detail'>
                    <div className='flex gap1'>
                        <div>
                            <p>เข้าเรียน</p>
                            {(getSemestersLoading || getCoursesLoading) ? (
                                <Skeleton variant="text" width={52} height={37.5} />
                            ) : (
                                <h4 className='semester'>{semesterName}</h4>
                            )}
                        </div>
                        <div>
                            <p>หลักสูตร</p>
                            {(getSemestersLoading || getCoursesLoading) ? (
                                <>
                                    <Skeleton variant="text" width={260} height={37.5} />
                                    <Skeleton variant="rect" width={400} height={20.5} />
                                </>
                            ) : (
                                <>
                                    <h4 className='course-name'>{courseName}</h4>
                                    <p className='desc'>{courseDesc}</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="ImportExcelDialog-upload-container">
                    <div className={`ImportExcelDialog-upload ${excelFile !== null ? 'active' : ''}`}>
                        <input
                            accept=".xlsx, .xls"
                            className="ImportExcelDialog-upload-input"
                            id="upload-excel-file"
                            type="file"
                            onChange={handleOnFileChange}
                            disabled={loading}
                        />
                        <label htmlFor="upload-excel-file">Upload your excel file here</label>
                        {
                            (excelFile) &&
                            <div className="ProgressDialog-file-item">
                                <img src={attachFileIcon} alt="fileIcon" />
                                <div className="ProgressDialog-file-detail">
                                    <a className="ProgressDialog-file-name" href={URL.createObjectURL(excelFile)} target="_blank" title="Click to download file">{excelFile.name}</a>
                                    <p className="ProgressDialog-file-desc">{Math.round((excelFile.size / 1024 + Number.EPSILON) * 100) / 100} KB</p>
                                </div>
                                <div className='flex gap-dot25'>
                                    <button className='ProgressDialog-delete-file-btn' onClick={handleOnFileDelete} title="Click to delete file" disabled={loading}>
                                        <img src={deleteIcon} alt="deleteIcon" />
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                    <div className='example-files'>
                        <p className='desc'>ตัวอย่างไฟล์ Excel</p>
                        {isStudent ? (
                            <div className='flex gap1'>
                                <a href={studentsTemplate} rel="noreferrer">ข้อมูลนิสิต</a>
                            </div>
                        ) : (
                            <div className='flex gap1'>
                                <a href={commonGoalTemplate1} rel="noreferrer">ป.โท ก1</a>
                                <a href={commonGoalTemplate2} rel="noreferrer">ป.โท ก2</a>
                                <a href={commonGoalTemplate3} rel="noreferrer">ป.เอก 1.1</a>
                                <a href={commonGoalTemplate4} rel="noreferrer">ป.เอก 1.2</a>
                                <a href={commonGoalTemplate5} rel="noreferrer">ป.เอก 2.1</a>
                                <a href={commonGoalTemplate6} rel="noreferrer">ป.เอก 2.2</a>
                            </div>
                        )}
                    </div>
                    <div className="ProgressDialog-bottom-container" >
                        <div className="ProgressDialog-actions" style={{ marginLeft: 'auto' }}>
                            <button className="ProgressDialog-button" type='button' onClick={handleClickClose}>Cancel</button>
                            <button className="ProgressDialog-button" type="submit" disabled={(loading || getSemestersLoading || getCoursesLoading || excelFile === null)} onClick={handleClickAdd}>Add</button>
                        </div>
                    </div>
                </div>
            </div>
            {
                (snackbarMessage) &&
                <SnackbarAlert severity={snackbarMessage.severity} message={snackbarMessage.message} />
            }
        </div>
    )
}

export default AddNewCommonGoalAndStudents